import apiClient, { uploadFilesByType } from 'api/base';
import { GetAllItemsParams } from 'interfaces';
import { DraftTask, Task } from 'models/task.model';

export const getTasks = async ({
  status,
  isOwner,
  favourites,
}: GetAllItemsParams): Promise<Task[]> => {
  const queries = [];

  if (status) {
    queries.push(`status=${status}`);
  }
  if (isOwner) {
    queries.push(`isOwner=${isOwner}`);
  }
  if (favourites) {
    queries.push('favourites=true');
  }

  const query = queries.join('&');
  const { data } = await apiClient.get(`/tasks${query ? `?${query}` : ''}`);

  return data;
};

export const getTask = async (id: string): Promise<Task> => {
  const { data } = await apiClient.get(`/tasks/${id}`);

  return data;
};

export const deleteTask = async (id: string): Promise<Task> => {
  const { data } = await apiClient.delete(`/tasks/${id}`);

  return data;
};

export const draftTask = async (
  task: Omit<DraftTask, 'status' | 'attachments'>,
): Promise<Task> => {
  const { data } = await apiClient.post('/tasks', { ...task });

  return data;
};

export const updateTask = async (
  id: string,
  task: Omit<DraftTask, 'status'>,
): Promise<Task> => {
  const { data } = await apiClient.put(`/tasks/${id}`, task);

  return data;
};

export const reorderTask = async (id: string, order: string): Promise<Task> => {
  const { data } = await apiClient.put(`/tasks/${id}/reorder`, { order });

  return data;
};

export const approveTask = async (id: string): Promise<Task> => {
  const { data } = await apiClient.patch(`/tasks/${id}/approve`);

  return data;
};

export const rejectTask = async (id: string): Promise<Task> => {
  const { data } = await apiClient.patch(`/tasks/${id}/reject`);

  return data;
};

export const reviewTask = async (id: string): Promise<Task> => {
  const { data } = await apiClient.patch(`/tasks/${id}/review`);

  return data;
};

export const reopenTask = async (id: string): Promise<Task> => {
  const { data } = await apiClient.patch(`/tasks/${id}/reopen`);

  return data;
};

export const uploadFiles = async (
  id: string,
  attachments: File[],
): Promise<Task> => {
  await uploadFilesByType(id, attachments, 'tasks');

  const data = await getTask(id);

  return data;
};

export const createTaskAndReview = async (
  task: Omit<DraftTask, 'status' | 'attachments'>,
  attachments: File[],
): Promise<Task> => {
  try {
    const updatedTask = await draftTask(task);

    if (attachments.length) {
      await uploadFiles(updatedTask.id, attachments);
    }

    const data = await reviewTask(updatedTask.id);

    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};

export const updateTaskAndReview = async (
  task: Omit<Task, 'status' | 'author' | 'createdAt' | 'updatedAt'>,
): Promise<Task> => {
  try {
    const updatedTask = await updateTask(task.id, {
      ...task,
      attachments: task.attachments?.filter(({ id }) => id) || [],
    });

    const notSavedAttachments = task.attachments?.filter(({ id }) => !id) || [];

    if (notSavedAttachments?.length) {
      await uploadFiles(updatedTask.id, notSavedAttachments);
    }

    const data = await reviewTask(updatedTask.id);

    return data;
  } catch (error: unknown) {
    return Promise.reject(error);
  }
};
